import { useQuery } from '@tanstack/react-query';
import { ApiError, api } from '../../api/client';
import { absoluteDate, unavailableExplanation, unavailableLabel } from '../../shared/format';
import { IconDocument, IconExternal, IconInfo, IconLock, ToolArt } from '../../shared/icons';
import type { Route } from '../../shared/router';
import { useLaunchTool } from './useLaunchTool';

interface ToolPageProps {
  toolKey: string;
  navigate: (route: Route) => void;
}

export function ToolPage({ toolKey, navigate }: ToolPageProps) {
  const query = useQuery({
    queryKey: ['tool', toolKey],
    queryFn: () => api.tool(toolKey),
    retry: false,
  });
  const launch = useLaunchTool();

  if (query.isPending) {
    return <div className="page-state">Loading…</div>;
  }

  if (query.isError) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    return (
      <div className="page-state">
        <p>{notFound ? 'That tool is not in the catalogue.' : 'This tool could not be loaded.'}</p>
        <button className="btn" onClick={() => navigate({ name: 'dashboard' })}>
          Back to tools
        </button>
      </div>
    );
  }

  const tool = query.data;

  return (
    <>
      <div className="panel tool-hero">
        <div className="tool-hero-art">
          <ToolArt toolKey={tool.key} width={250} height={130} />
        </div>

        <div className="tool-hero-body">
          <div className="tool-hero-top">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
              <div className="tool-hero-title">
                <h1 className="tool-hero-name">{tool.name}</h1>
                <span className={`badge ${tool.launchable ? 'badge-ok' : 'badge-neutral'}`}>
                  {unavailableLabel(tool.unavailable_reason)}
                </span>
                {tool.version && <span className="chip">v{tool.version}</span>}
              </div>
              <p className="tool-hero-summary">{tool.summary}</p>
            </div>

            <div className="tool-hero-actions">
              {tool.docs_url && (
                <a className="btn" href={tool.docs_url} target="_blank" rel="noreferrer">
                  <IconDocument size={12} />
                  Documentation
                </a>
              )}
              {tool.launchable ? (
                <button
                  className="btn btn-primary btn-lg"
                  onClick={() => launch.mutate(tool.key)}
                  disabled={launch.isPending}
                >
                  <IconExternal size={13} />
                  {launch.isPending ? 'Opening…' : 'Launch'}
                </button>
              ) : (
                <button className="btn btn-lg" disabled>
                  <IconLock size={13} />
                  Launch
                </button>
              )}
            </div>
          </div>

          <div className="stat-grid">
            <div className="stat">
              <div className="stat-label">Design codes</div>
              <div className="stat-value">{tool.codes?.join(' · ') || '—'}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Release</div>
              <div className="stat-value">{tool.version ? `v${tool.version}` : '—'}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Your access</div>
              <div className="stat-value">{tool.granted ? 'Granted' : 'Not granted'}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Access expires</div>
              <div className="stat-value">{tool.expires_at ? absoluteDate(tool.expires_at) : 'No end date'}</div>
            </div>
          </div>
        </div>
      </div>

      {launch.isError && (
        <div className="notice notice-error" role="alert">
          <IconInfo size={14} />
          <span>
            {launch.error instanceof ApiError
              ? launch.error.message
              : 'The tool could not be opened. Try again.'}
          </span>
        </div>
      )}

      {!tool.launchable && (
        <div className="notice notice-warn">
          <IconInfo size={14} />
          <span>{unavailableExplanation(tool.unavailable_reason)}</span>
        </div>
      )}
    </>
  );
}
